import { Input, OnChanges, OnDestroy, OnInit, SimpleChanges, Directive } from "@angular/core";
import { Color, Label, SingleOrMultiDataSet } from "ng2-charts";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, Subscription } from "rxjs";
import { debounceTime, finalize, tap } from "rxjs/operators";
import { ChartDataSets, ChartOptions, ChartType } from "chart.js";
import labelPlugin from "chartjs-plugin-labels";
import { PersianService } from "projects/sepidz-web-framework/src/lib/services/persian.service";
import { NameValue } from "projects/sepidz-web-framework/src/lib/models/name-value";

export type ChartDataList = SingleOrMultiDataSet;
export type ChartDatasetList = ChartDataSets[];

@Directive()
export abstract class ChartComponent implements OnInit, OnDestroy, OnChanges {
  private reloadSubscription: Subscription;
  private loadSubscription: Subscription;
  @Input() isLoading = true;
  @Input() data: any[];
  @Input() apiName: string;
  @Input() areaName: string;
  @Input() title: string;
  @Input() ratio = 1;
  @Input() stackedX = true;
  @Input() stackedY = true;
  @Input() legend = false;
  @Input() legendLabels: string[] = [];
  @Input() filters: NameValue[];
  @Input() tooltipEnabled = false;
  @Input() labelEnabled = false;
  @Input() reload: Observable<boolean>;
  @Input() countable = true;
  @Input() colors: Color[] = [
    {
      // dark grey
      backgroundColor: "rgba(77,83,96,0.2)",
      borderColor: "rgba(77,83,96,1)",
      pointBackgroundColor: "rgba(77,83,96,1)",
      pointBorderColor: "#fff",
      pointHoverBackgroundColor: "#fff",
      pointHoverBorderColor: "rgba(77,83,96,1)",
    },
    {
      // cyan
      backgroundColor: "#80CBC4",
      borderColor: "#00897B",
      pointBackgroundColor: "#00897B",
      pointBorderColor: "#fff",
      pointHoverBackgroundColor: "#fff",
      pointHoverBorderColor: "#00897B",
    },
    {
      // red
      backgroundColor: "rgba(255,0,0,0.3)",
      borderColor: "red",
      pointBackgroundColor: "rgba(148,159,177,1)",
      pointBorderColor: "#fff",
      pointHoverBackgroundColor: "#fff",
      pointHoverBorderColor: "rgba(148,159,177,0.8)",
    },
  ];
  public plugins = [labelPlugin];
  public datasetList: ChartDatasetList = [{ data: [] }];
  public dataList: ChartDataList = [];
  public labels: Label[] = [];
  public options: ChartOptions;

  protected constructor(protected httpClient: HttpClient, protected persian: PersianService) {}

  protected abstract type: ChartType;

  protected abstract loaded(data: any[]);

  protected init() {
    this.options = {
      elements: {
        arc: {
          borderWidth: 0,
        },
      },
      tooltips: {
        enabled: this.tooltipEnabled,
        mode: "index",
        intersect: false,
        titleFontFamily: "IRANSansX",
        titleFontStyle: "400",
        titleFontSize: 12,
        bodyFontFamily: "IRANSansX",
        bodyFontSize: 12,
      },
      responsive: true,
      scales: {
        xAxes: [
          {
            gridLines: {
              color: "rgba(0,0,0,0.02)",
              zeroLineColor: "rgba(0,0,0,0.02)",
            },
            stacked: this.stackedX,
            ticks: {
              beginAtZero: true,
              fontSize: 12,
              fontFamily: "IRANSansX",
              callback: (label, index, labels) => {
                if (isNaN(label as any)) {
                  return label;
                }
                return this.persian.toPersianNumber(label as any);
              },
            },
          },
        ],
        yAxes: [
          {
            gridLines: {
              color: "rgba(0,0,0,0.02)",
              zeroLineColor: "rgba(0,0,0,0.02)",
            },
            stacked: this.stackedY,
            ticks: {
              beginAtZero: true,
              fontSize: 12,
              fontFamily: "IRANSansX",
              callback: (label, index, labels) => {
                if (isNaN(label as any)) {
                  return label;
                }

                if (this.datasetList.every((d) => !d.data.length)) {
                  return;
                }
                label = (label as number) / this.ratio;
                return this.countable === true && label % 1 !== 0 ? undefined : this.persian.toPersianNumber(label as any);
              },
            },
          },
        ],
      },
      legend: {
        display: this.legend,
        position: "bottom",
        labels: {
          fontSize: 12,
          fontFamily: "IRANSansX",
        },
      },
      plugins: {
        labels: {
          render: (args) => {
            return this.labelEnabled ? args.value : "";
          },
          fontFamily: "IRANSansX",
          fontSize: 18,
        },
      },
    };

    for (let i = 0; i < this.legendLabels.length; i++) {
      this.datasetList[i] = { data: [], label: this.legendLabels[i] };
    }
  }

  ngOnInit(): void {
    this.init();
    if (this.apiName && !this.data) {
      this.load();
      this.reloading();
    } else if (this.data) {
      this.isLoading = false;
      this.loaded(this.data);
    }
  }

  private reloading() {
    if (!this.reload) {
      return;
    }

    this.reloadSubscription = this.reload
      .pipe(
        debounceTime(300),
        tap((value) => {
          if (value) {
            this.load();
          }
        })
      )
      .subscribe();
  }

  private load() {
    this.labels.length = 0;
    this.dataList.length = 0;
    const params = this.buildParams();
    this.loadSubscription = this.httpClient
      .get(`${this.apiName}`, { params })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((data: any[]) => {
        this.loaded(data);
      });
  }

  buildParams(): HttpParams | undefined {
    if (!this.filters) {
      return;
    }
    let params = new HttpParams();
    this.filters.forEach((f) => (params = params.append(f.Name, f.Value)));
    return params;
  }

  ngOnDestroy(): void {
    if (this.loadSubscription) {
      this.loadSubscription.unsubscribe();
    }
    if (this.reloadSubscription) {
      this.reloadSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["legendLabels"] && !changes["legendLabels"].isFirstChange()) {
      this.datasetList.forEach((dataset, i) => (dataset.label = changes["legendLabels"]["currentValue"][i]));
    }
    if (changes["data"] && !changes["data"].isFirstChange()) {
      this.labels.length = 0;
      this.dataList.length = 0;
      this.isLoading = false;
      if (changes["data"]["currentValue"]) {
        this.loaded(changes["data"]["currentValue"]);
      }
    }
  }
}
